import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Image, ScrollView, Text, View } from "react-native";

import { listAllJudgesForAdmin, type AdminJudgeRow } from "../../../lib/judges";
import { exportRowsAsCsv, fmtCellDate, todayDateForFilename } from "../../../lib/csvExport";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import {
  AdminAvatar,
  AdminButton,
  AdminEmpty,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  FilterChip,
  ListRow,
  ListRowGroup,
} from "../../../components/admin/ui";
import { StatusPill, type StatusPillTone } from "../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;
const TZ = "America/New_York";

type StatusFilter = "all" | "active" | "inactive" | "pending_invite";
type PhotoFilter = "any" | "has" | "missing";

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: "All",
  active: "Active",
  inactive: "Inactive",
  pending_invite: "Pending invite",
};

const STATUS_TONE: Record<AdminJudgeRow["status"], StatusPillTone> = {
  active: "sage",
  inactive: "grey",
  pending_invite: "amber",
};

const STATUS_TEXT: Record<AdminJudgeRow["status"], string> = {
  active: "Active",
  inactive: "Inactive",
  pending_invite: "Pending invite",
};

const PHOTO_FILTERS: { key: PhotoFilter; label: string }[] = [
  { key: "any", label: "Any photo" },
  { key: "has", label: "Has photo" },
  { key: "missing", label: "No photo" },
];

function fmtRelative(iso: string | null): string {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "moments ago";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ });
}

export default function AdminJudgesScreen() {
  const [rows, setRows] = useState<AdminJudgeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const filter = useAdminListFilter({
    keys: ["q", "status", "photo"],
    defaults: { q: "", status: "all", photo: "any" },
  });

  const search = filter.get("q");
  const statusVal = filter.get("status") as StatusFilter;
  const photoVal = filter.get("photo") as PhotoFilter;
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) filter.set({ q: searchInput });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      setRows(await listAllJudgesForAdmin());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load judges");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusVal !== "all" && r.status !== statusVal) return false;
      if (photoVal === "has" && !r.avatar_url) return false;
      if (photoVal === "missing" && r.avatar_url) return false;
      if (q) {
        const hay = `${r.full_name ?? ""} ${r.email}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, statusVal, photoVal]);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="CRM · Judges"
          title="All judges"
          subtitle="Industry reviewers across all competitions."
          actions={
            <>
              <AdminButton
                theme={THEME}
                label="Export CSV"
                variant="ghost"
                size="sm"
                onPress={() =>
                  exportRowsAsCsv({
                    filename: `judges_${todayDateForFilename()}.csv`,
                    auditPage: "judges",
                    columns: [
                      { header: "Name", get: (r: AdminJudgeRow) => r.full_name ?? "" },
                      { header: "Email", get: (r: AdminJudgeRow) => r.email },
                      { header: "Status", get: (r: AdminJudgeRow) => r.status },
                      { header: "Organization", get: (r: AdminJudgeRow) => r.organization ?? "" },
                      { header: "Assigned competitions", get: (r: AdminJudgeRow) => r.assigned_count },
                      { header: "Completed scores", get: (r: AdminJudgeRow) => r.scored_count },
                      { header: "Last activity", get: (r: AdminJudgeRow) => fmtCellDate(r.last_activity_at) },
                    ],
                    rows: filtered,
                  })
                }
              />
              <AdminButton theme={THEME} label="+ Invite judge" onPress={() => router.push("/admin/invites?kind=judge" as Href)} />
            </>
          }
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}

        {/* Filter bar */}
        <View style={{ padding: 12, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border, gap: 10 }}>
          <AdminInput theme={THEME} value={searchInput} onChangeText={setSearchInput} placeholder="Search by name or email…" />
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>Status:</Text>
            {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((k) => (
              <FilterChip key={k} theme={THEME} label={STATUS_LABELS[k]} active={statusVal === k} onPress={() => filter.set({ status: k })} />
            ))}
          </View>
          <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>Photo:</Text>
            {PHOTO_FILTERS.map((p) => (
              <FilterChip key={p.key} theme={THEME} label={p.label} active={photoVal === p.key} onPress={() => filter.set({ photo: p.key })} />
            ))}
          </View>
        </View>

        <View style={{ marginTop: SPACING.md }}>
          {loading ? (
            <AdminLoading theme={THEME} label="Loading judges…" />
          ) : filtered.length === 0 ? (
            <AdminEmpty
              theme={THEME}
              title="No judges match these filters."
              body={search || statusVal !== "all" || photoVal !== "any" ? "Try clearing the filters." : undefined}
              ctaLabel={search || statusVal !== "all" || photoVal !== "any" ? "Clear filters" : undefined}
              onCta={() => {
                setSearchInput("");
                filter.set({ q: "", status: "all", photo: "any" });
              }}
            />
          ) : (
            <ListRowGroup theme={THEME}>
              {filtered.map((row, i) => (
                <ListRow
                  key={row.id}
                  theme={THEME}
                  first={i === 0}
                  onPress={() => {
                    if (row.user_id) router.push(`/admin/judge/${row.user_id}` as Href);
                    else router.push(`/admin/invites?kind=judge` as Href);
                  }}
                  leading={
                    row.avatar_url ? (
                      <Image source={{ uri: row.avatar_url }} style={{ width: 36, height: 36, borderRadius: RADII.full }} />
                    ) : (
                      <AdminAvatar theme={THEME} name={row.full_name ?? row.email} />
                    )
                  }
                  title={row.full_name ?? row.email}
                  subtitle={
                    <View>
                      <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }} numberOfLines={1}>
                        {row.email}{row.organization ? ` · ${row.organization}` : ""}
                      </Text>
                      <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }} numberOfLines={1}>
                        {row.assigned_count} assigned · {row.scored_count} scored · {fmtRelative(row.last_activity_at)}
                      </Text>
                    </View>
                  }
                  trailing={<StatusPill theme={THEME} label={STATUS_TEXT[row.status]} tone={STATUS_TONE[row.status]} />}
                />
              ))}
            </ListRowGroup>
          )}
        </View>
      </View>
    </ScrollView>
  );
}
